import { Course, Assignment, Student } from '../types';
import { fetchWithTimeout } from '../lib/fetchWithTimeout';
import { withRetry } from '../lib/retry';

const CLASSROOM_BASE_URL = 'https://classroom.googleapis.com/v1';
const GMAIL_BASE_URL = 'https://gmail.googleapis.com/gmail/v1';

/** Minimal shapes from Classroom REST JSON (we only map fields we use). */
type GApiCourse = { id?: string; name?: string; section?: string };
type GApiCoursesList = { courses?: GApiCourse[] };
type GApiCourseWork = {
  id?: string;
  title?: string;
  maxPoints?: number;
  description?: string;
};
type GApiCourseWorkList = { courseWork?: GApiCourseWork[] };
type GApiStudent = {
  userId?: string;
  profile?: { name?: { fullName?: string }; emailAddress?: string };
};
type GApiStudentsList = { students?: GApiStudent[] };
type GApiStudentSubmission = { id?: string; userId?: string };
type GApiSubmissionsList = { studentSubmissions?: GApiStudentSubmission[] };

type StatusError = Error & { status?: number };

/** Optional hooks: silent/interactive GIS refresh on 401 (roadmap §4.2). */
export type ClassroomTokenHooks = {
  refreshAccessToken?: () => Promise<string | null>;
  onTokenRotated?: (accessToken: string) => void;
};

function flattenHeaders(h: HeadersInit | undefined): Record<string, string> {
  if (!h) return {};
  if (h instanceof Headers) return Object.fromEntries(h.entries());
  if (Array.isArray(h)) return Object.fromEntries(h);
  return { ...(h as Record<string, string>) };
}

/**
 * Google Classroom + Gmail REST client. Pass a short-lived OAuth access token;
 * optional {@link ClassroomTokenHooks} enable silent refresh on 401.
 * Normalized LMS shapes for future providers: `services/lms/types.ts`.
 */
export class ClassroomService {
  private accessToken: string;
  private tokenHooks?: ClassroomTokenHooks;

  /** @param token - OAuth 2.0 access token with Classroom (and optionally Gmail) scopes */
  constructor(token: string, tokenHooks?: ClassroomTokenHooks) {
    this.accessToken = token;
    this.tokenHooks = tokenHooks;
  }

  /**
   * Classroom / Gmail JSON APIs: timeout + retry on 429/5xx (roadmap §1.3).
   */
  private async fetchGoogleAuthorized(
    url: string,
    options: RequestInit = {},
    canRefreshOn401 = true
  ): Promise<Response> {
    return withRetry(
      async () => {
        let response: Response;
        const headerBase = flattenHeaders(options.headers);
        try {
          response = await fetchWithTimeout(url, {
            ...options,
            timeoutMs: 45_000,
            headers: {
              ...headerBase,
              Authorization: `Bearer ${this.accessToken}`,
            },
          });
        } catch (err) {
          if (err instanceof DOMException && err.name === 'AbortError') {
            throw new Error('Request timed out. Check your connection and try again.');
          }
          if (err instanceof TypeError && err.message === 'Failed to fetch') {
            throw new Error('Network connection lost. Please check your internet.');
          }
          throw err;
        }

        if (response.ok) return response;

        const status = response.status;

        if (status === 401 && canRefreshOn401 && this.tokenHooks?.refreshAccessToken) {
          const next = await this.tokenHooks.refreshAccessToken();
          if (next) {
            this.accessToken = next;
            this.tokenHooks.onTokenRotated?.(next);
            return this.fetchGoogleAuthorized(url, options, false);
          }
        }

        let errorMessage = 'Google API error';
        try {
          const body = await response.json();
          errorMessage = body.error?.message || errorMessage;
        } catch {
          try {
            errorMessage = (await response.text()) || errorMessage;
          } catch {
            /* ignore */
          }
        }

        if (status >= 500 || status === 429) {
          const e = new Error(errorMessage) as StatusError;
          e.status = status;
          throw e;
        }

        const prefix = url.includes('classroom.googleapis.com') ? 'Classroom: ' : '';
        const clientErr = new Error(`${prefix}${errorMessage}`) as StatusError;
        clientErr.status = status;
        throw clientErr;
      },
      {
        maxAttempts: 3,
        delayMs: 1000,
        backoff: 'exponential',
        retryable: (e) => {
          const s = (e as StatusError).status;
          if (typeof s === 'number') return s >= 500 || s === 429;
          // Transient network only — do not retry 4xx-style thrown Errors (no .status).
          return e instanceof TypeError;
        },
      }
    );
  }

  private async fetchWithAuth(endpoint: string, options: RequestInit = {}) {
    try {
      const response = await this.fetchGoogleAuthorized(`${CLASSROOM_BASE_URL}${endpoint}`, {
        ...options,
        headers: {
          ...options.headers,
          Authorization: `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      // Some Classroom endpoints (like DELETE) return 204 No Content.
      if (response.status === 204 || response.status === 205) {
        return null;
      }

      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        return response.json();
      }

      // Fallback: return plain text if not JSON
      return response.text();
    } catch (err) {
      if (err instanceof TypeError && err.message === 'Failed to fetch') {
        throw new Error('Network connection lost. Please check your internet.');
      }
      throw err;
    }
  }

  async getCourses(): Promise<Course[]> {
    // `teacherId=me` restricts the response to courses the
    // signed-in user owns or co-teaches. DoneGrading must NEVER
    // surface a Classroom course in which the user is enrolled as
    // a student, so this filter is the load-bearing guard on the
    // client-side helper as well. The server-side `/api/classroom/courses`
    // endpoint is authoritative and applies the same filter; this
    // helper exists for the legacy LMS-abstraction path and keeps
    // the contract identical.
    const data = (await this.fetchWithAuth(
      '/courses?teacherId=me&courseStates=ACTIVE'
    )) as GApiCoursesList;
    return (data.courses || []).map((c) => ({
      id: c.id ?? '',
      name: c.name ?? 'Course',
      period: c.section || 'General',
      source: 'google' as const,
    }));
  }

  async createCourse(name: string, period: string): Promise<Course> {
    const data = await this.fetchWithAuth('/courses', {
      method: 'POST',
      body: JSON.stringify({
        name,
        section: period || 'General',
        // Let the current authenticated teacher be the owner
        ownerId: 'me',
      }),
    });
    return {
      id: data.id,
      name: data.name,
      period: data.section || 'General',
      source: 'google',
      lastUsed: Date.now(),
    };
  }

  async getAssignments(courseId: string): Promise<Assignment[]> {
    const data = (await this.fetchWithAuth(
      `/courses/${courseId}/courseWork`
    )) as GApiCourseWorkList;
    return (data.courseWork || []).map((cw) => ({
      id: cw.id ?? '',
      title: cw.title ?? 'Assignment',
      maxScore: cw.maxPoints || 100,
      rubric: cw.description || 'Grade based on assignment title.',
    }));
  }

  async getStudents(courseId: string): Promise<Student[]> {
    const data = (await this.fetchWithAuth(`/courses/${courseId}/students`)) as GApiStudentsList;
    return (data.students || []).map((s) => ({
      id: s.userId ?? '',
      name: s.profile?.name?.fullName || 'Unknown Student',
      email: s.profile?.emailAddress || undefined, // Extracted email address for Gmail API
    }));
  }

  async createAssignment(
    courseId: string,
    title: string,
    description: string,
    maxScore: number
  ): Promise<Assignment> {
    const data = await this.fetchWithAuth(`/courses/${courseId}/courseWork`, {
      method: 'POST',
      body: JSON.stringify({
        title,
        description,
        maxPoints: maxScore,
        workType: 'ASSIGNMENT',
        state: 'PUBLISHED',
      }),
    });
    return {
      id: data.id,
      title: data.title,
      maxScore: data.maxPoints || 100,
      rubric: data.description || '',
    };
  }

  async updateCourse(courseId: string, name: string, period?: string): Promise<Course> {
    const data = await this.fetchWithAuth(`/courses/${courseId}?updateMask=name,section`, {
      method: 'PATCH',
      body: JSON.stringify({
        name,
        section: period || 'General',
      }),
    });
    return {
      id: data.id,
      name: data.name,
      period: data.section || 'General',
      source: 'google',
      lastUsed: Date.now(),
    };
  }

  async deleteCourse(courseId: string): Promise<void> {
    await this.fetchWithAuth(`/courses/${courseId}`, {
      method: 'DELETE',
    });
  }

  async updateAssignment(
    courseId: string,
    assignmentId: string,
    title: string
  ): Promise<Assignment> {
    const data = await this.fetchWithAuth(
      `/courses/${courseId}/courseWork/${assignmentId}?updateMask=title`,
      {
        method: 'PATCH',
        body: JSON.stringify({ title }),
      }
    );
    return {
      id: data.id,
      title: data.title,
      maxScore: data.maxPoints || 100,
      rubric: data.description || '',
    };
  }

  async deleteAssignment(courseId: string, assignmentId: string): Promise<void> {
    await this.fetchWithAuth(`/courses/${courseId}/courseWork/${assignmentId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Posts draft + assigned grades and optional private comment. Requires the student to have
   * opened the assignment at least once (submission must exist). Call only when online; offline
   * queuing is handled in the app shell (`lib/offlineGradeSyncIntent.ts`).
   */
  async postGrade(
    courseId: string,
    courseWorkId: string,
    studentId: string,
    score: number,
    feedback: string
  ) {
    // 1. Fetch ONLY this specific student's submission
    const submissionsData = (await this.fetchWithAuth(
      `/courses/${courseId}/courseWork/${courseWorkId}/studentSubmissions?userId=${studentId}`
    )) as GApiSubmissionsList;

    const submissions = submissionsData.studentSubmissions || [];

    // 2. Find the submission object
    const submission = submissions.find((s) => String(s.userId) === String(studentId));

    if (!submission) {
      throw new Error(
        `Grade sync failed: No submission found. The student must at least open the assignment once in Google Classroom.`
      );
    }

    // 3. Patch BOTH draft and assigned grades
    const numericScore = Number(score);

    await this.fetchWithAuth(
      `/courses/${courseId}/courseWork/${courseWorkId}/studentSubmissions/${submission.id}?updateMask=draftGrade,assignedGrade`,
      {
        method: 'PATCH',
        body: JSON.stringify({
          draftGrade: numericScore,
          assignedGrade: numericScore,
        }),
      }
    );

    // 4. Add private comment for feedback (Kept here to act as a fallback/log if Google ever enables this via API)
    if (feedback && feedback.trim()) {
      try {
        await this.fetchWithAuth(
          `/courses/${courseId}/courseWork/${courseWorkId}/studentSubmissions/${submission.id}/comments`,
          {
            method: 'POST',
            body: JSON.stringify({ text: feedback }),
          }
        );
      } catch (e) {
        console.warn('Grade posted successfully, but feedback comment failed:', e);
      }
    }

    return true;
  }

  async sendGradeEmail(
    toEmail: string,
    subject: string,
    body: string,
    imageBase64?: string | string[]
  ): Promise<void> {
    if (!toEmail) return;

    const escapeHtml = (s: string) =>
      s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    const textBody = body ?? '';
    const safeText = escapeHtml(textBody);
    const htmlBody = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${escapeHtml(subject || 'DoneGrading')}</title>
  </head>
  <body style="margin:0;padding:0;background:#f6f7fb;">
    <div style="max-width:640px;margin:0 auto;padding:20px 14px;">
      <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;color:#0f172a;">
        <div style="padding:2px 2px 12px 2px;">
          <div style="font-size:12px;letter-spacing:0.12em;text-transform:uppercase;color:#64748b;font-weight:700;">
            DoneGrading feedback
          </div>
        </div>
        <div style="background:#ffffff;border:1px solid #e5e7eb;border-radius:16px;padding:16px 16px;box-shadow:0 8px 20px rgba(15,23,42,0.06);">
          <div style="font-size:15px;line-height:1.6;white-space:pre-wrap;word-wrap:break-word;font-weight:500;">
            ${safeText}
          </div>
        </div>
        <div style="padding:12px 4px 0 4px;font-size:12px;color:#64748b;">
          Sent from DoneGrading
        </div>
      </div>
    </div>
  </body>
</html>`;

    const mixedBoundary = 'DONEGRADING-MIXED-BOUNDARY';
    const altBoundary = 'DONEGRADING-ALT-BOUNDARY';

    let mimeBody: string;
    const images = Array.isArray(imageBase64) ? imageBase64 : imageBase64 ? [imageBase64] : [];
    if (images.length > 0) {
      mimeBody = `MIME-Version: 1.0
Content-Type: multipart/mixed; boundary="${mixedBoundary}"

--${mixedBoundary}
Content-Type: multipart/alternative; boundary="${altBoundary}"

--${altBoundary}
Content-Type: text/plain; charset="UTF-8"

${textBody}

--${altBoundary}
Content-Type: text/html; charset="UTF-8"

${htmlBody}

--${altBoundary}--
${images
  .slice(0, 10)
  .map(
    (img, idx) =>
      `
--${mixedBoundary}
Content-Type: image/jpeg
Content-Transfer-Encoding: base64
Content-Disposition: attachment; filename="work-${idx + 1}.jpg"

${img}
`
  )
  .join('')}
--${mixedBoundary}--
`;
    } else {
      mimeBody = `MIME-Version: 1.0
Content-Type: multipart/alternative; boundary="${altBoundary}"

--${altBoundary}
Content-Type: text/plain; charset="UTF-8"

${textBody}

--${altBoundary}
Content-Type: text/html; charset="UTF-8"

${htmlBody}

--${altBoundary}--`;
    }

    const fullMessage = `To: ${toEmail}
Subject: ${subject}
${mimeBody}`;

    const base64Encoded = btoa(unescape(encodeURIComponent(fullMessage)));
    const raw = base64Encoded.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

    await this.fetchGoogleAuthorized(`${GMAIL_BASE_URL}/users/me/messages/send`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ raw }),
    });
  }

  /**
   * Send a formatted lesson plan (HTML + plain-text alternative) via Gmail API.
   * `fullHtmlDocument` must be a complete HTML document (caller escapes user content).
   */
  /**
   * `toEmail` may be a single address or comma-separated recipients (Gmail raw `To:` line).
   */
  async sendLessonPlanEmail(
    toEmail: string,
    subject: string,
    plainText: string,
    fullHtmlDocument: string
  ): Promise<void> {
    if (!toEmail?.trim()) return;

    const textBody = plainText ?? '';
    const altBoundary = 'DONEGRADING-PLAN-ALT';

    const mimeBody = `MIME-Version: 1.0
Content-Type: multipart/alternative; boundary="${altBoundary}"

--${altBoundary}
Content-Type: text/plain; charset="UTF-8"

${textBody}

--${altBoundary}
Content-Type: text/html; charset="UTF-8"

${fullHtmlDocument}

--${altBoundary}--`;

    const fullMessage = `To: ${toEmail}
Subject: ${subject}
${mimeBody}`;

    const base64Encoded = btoa(unescape(encodeURIComponent(fullMessage)));
    const raw = base64Encoded.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

    await this.fetchGoogleAuthorized(`${GMAIL_BASE_URL}/users/me/messages/send`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ raw }),
    });
  }
}
